import { useState, useEffect, useCallback } from "react";
import api from "../api";

const ORANGE = "#EE6A1A";
const VERDE = "#1D9E75";
const VERM = "#e2445c";
const AZUL = "#579bfc";

const dois = (n) => String(n).padStart(2, "0");
const fmtDia = (d) => { const x = new Date(d); return dois(x.getDate()) + "/" + dois(x.getMonth() + 1); };
const fmtHora = (d) => { const x = new Date(d); return dois(x.getHours()) + ":" + dois(x.getMinutes()); };
const reais = (v) => "R$ " + Number(v || 0).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const reaisCurto = (v) => "R$ " + Number(v || 0).toLocaleString("pt-BR", { maximumFractionDigits: 0 });
const horas = (min) => {
  const h = Math.floor((min || 0) / 60), m = (min || 0) % 60;
  if (!h && !m) return "livre";
  return h ? (h + "h" + (m ? dois(m) : "")) : (m + "min");
};

const paraWhats = (tel) => {
  const n = String(tel || "").replace(/\D/g, "");
  return n ? (n.length <= 11 ? "55" + n : n) : "";
};
const abrirWhats = (tel, msg) => {
  const f = paraWhats(tel);
  if (!f) return window.alert("Essa pessoa não tem telefone cadastrado.");
  window.open("https://wa.me/" + f + "?text=" + encodeURIComponent(msg), "_blank");
};

const card = { background: "var(--card-bg)", border: "1px solid var(--border-color)", borderRadius: 14, padding: 20 };
const tituloCard = (t, sub) => (
  <div style={{ marginBottom: 14 }}>
    <div style={{ fontSize: 14, fontWeight: 700, color: "var(--text-primary)" }}>{t}</div>
    {sub && <div style={{ fontSize: 12.5, color: "var(--text-muted)", marginTop: 3 }}>{sub}</div>}
  </div>
);

function Kpi({ titulo, valor, sub, cor }) {
  return (
    <div style={{ ...card, flex: "1 1 200px", minWidth: 200, padding: "18px 20px" }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: ".05em" }}>{titulo}</div>
      <div style={{ fontSize: 25, fontWeight: 800, color: cor || "var(--text-primary)", margin: "8px 0 2px", lineHeight: 1.1 }}>{valor}</div>
      <div style={{ fontSize: 12, color: "var(--text-secondary)" }}>{sub}</div>
    </div>
  );
}

// Barras de uma cor só: o número fica sempre escrito ao lado, nunca depende da cor.
function Barras({ dados, rotuloValor, vazio }) {
  const maior = dados.reduce((m, d) => Math.max(m, d.qtd || 0), 0) || 1;
  if (!dados.length) return <div style={{ fontSize: 13, color: "var(--text-muted)" }}>{vazio}</div>;
  return (
    <div>
      {dados.map((d) => (
        <div key={d.rotulo} style={{ marginBottom: 11 }}>
          <div style={{ display: "flex", justifyContent: "space-between", gap: 10, fontSize: 12.5, marginBottom: 4 }}>
            <span style={{ color: "var(--text-primary)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{d.rotulo}</span>
            <span style={{ color: "var(--text-secondary)", whiteSpace: "nowrap", fontWeight: 600 }}>{rotuloValor(d)}</span>
          </div>
          <div style={{ height: 7, borderRadius: 4, background: "var(--hover-bg)", overflow: "hidden" }}>
            <div style={{ height: "100%", width: Math.max(3, ((d.qtd || 0) / maior) * 100) + "%", background: ORANGE, borderRadius: 4 }} />
          </div>
        </div>
      ))}
    </div>
  );
}

function Linha({ esq, meio, dir, acao }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 12px", borderRadius: 9, background: "var(--hover-bg)", flexWrap: "wrap" }}>
      <div style={{ fontSize: 13, fontWeight: 800, color: "var(--text-primary)", minWidth: 48 }}>{esq}</div>
      <div style={{ flex: 1, minWidth: 130 }}>{meio}</div>
      {dir}
      {acao}
    </div>
  );
}

export default function Painel() {
  const [d, setD] = useState(null);
  const [erro, setErro] = useState("");
  const [carregando, setCarregando] = useState(true);

  const carregar = useCallback(() => {
    setCarregando(true);
    api.get("/painel")
      .then(({ data }) => { setD(data); setErro(""); })
      .catch((e) => setErro((e.response && e.response.data && e.response.data.message) || "Não foi possível carregar."))
      .finally(() => setCarregando(false));
  }, []);
  useEffect(() => { carregar(); }, [carregar]);

  const btn = { padding: "6px 12px", borderRadius: 8, border: "1px solid var(--border-color)", background: "transparent", color: "var(--text-secondary)", fontWeight: 700, fontSize: 12, cursor: "pointer", fontFamily: "inherit", whiteSpace: "nowrap" };

  if (carregando && !d) return <div style={{ padding: 24, fontSize: 13, color: "var(--text-muted)" }}>Carregando…</div>;
  if (erro) return <div style={{ padding: 24, fontSize: 13, color: VERM }}>{erro}</div>;
  if (!d) return null;

  const h = d.hoje, m = d.mes;
  const maiorDia = d.semana.reduce((x, s) => Math.max(x, s.minutos), 0) || 1;

  return (
    <div style={{ padding: 24, fontFamily: "'Inter',-apple-system,sans-serif", background: "var(--page-bg)", minHeight: "100vh" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, flexWrap: "wrap", marginBottom: 18 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 800, color: "var(--text-primary)" }}>Painel</h1>
          <p style={{ margin: "4px 0 0", fontSize: 13, color: "var(--text-muted)" }}>Como a clínica está hoje e no mês.</p>
        </div>
        <button onClick={carregar} style={btn}>Atualizar</button>
      </div>

      {/* hoje */}
      <div style={{ ...card, marginBottom: 16 }}>
        <div style={{ display: "flex", gap: 22, flexWrap: "wrap", alignItems: "baseline", marginBottom: 16 }}>
          <div>
            <div style={{ fontSize: 28, fontWeight: 800, color: "var(--text-primary)", lineHeight: 1 }}>{h.total}</div>
            <div style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 4 }}>horários hoje</div>
          </div>
          {[["confirmados", h.confirmados], ["ainda sem confirmar", h.aguardando], ["já atendidos", h.atendidos], ["faltaram", h.faltas]].map(([rot, val]) => (
            <div key={rot}>
              <div style={{ fontSize: 20, fontWeight: 700, color: "var(--text-secondary)", lineHeight: 1 }}>{val}</div>
              <div style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 4 }}>{rot}</div>
            </div>
          ))}
        </div>

        {h.aguardando > 0 && (
          <div style={{ fontSize: 12.5, color: "#b45309", background: "#b4530918", borderRadius: 9, padding: "10px 12px", marginBottom: 14, lineHeight: 1.5 }}>
            {h.aguardando} {h.aguardando === 1 ? "paciente ainda não confirmou" : "pacientes ainda não confirmaram"} o horário de hoje. Mandar a confirmação derruba a falta pela metade.
          </div>
        )}

        {!h.proximos.length && <div style={{ fontSize: 13, color: "var(--text-muted)" }}>Nenhum horário pela frente hoje.</div>}
        <div style={{ display: "flex", flexDirection: "column", gap: 7 }}>
          {h.proximos.map((a) => (
            <Linha key={a._id}
              esq={fmtHora(a.inicio)}
              meio={<>
                <div style={{ fontSize: 13, fontWeight: 600, color: "var(--text-primary)" }}>{a.pacienteNome}</div>
                <div style={{ fontSize: 11.5, color: "var(--text-secondary)", marginTop: 1 }}>
                  {a.procedimento || "sem procedimento"}{a.profissionalNome ? " · " + a.profissionalNome : ""}
                </div>
              </>}
              dir={<span style={{ fontSize: 11.5, fontWeight: 700, color: a.status === "confirmado" ? VERDE : "#b45309", background: (a.status === "confirmado" ? VERDE : "#b45309") + "18", borderRadius: 6, padding: "3px 9px", whiteSpace: "nowrap" }}>
                {a.status === "confirmado" ? "confirmado" : "sem confirmar"}
              </span>}
            />
          ))}
        </div>
      </div>

      {/* mes */}
      <div style={{ display: "flex", gap: 14, flexWrap: "wrap", marginBottom: 16 }}>
        <Kpi titulo="Realizado no mês" valor={reais(m.realizado)} sub={m.atendidos + " atendimentos"} cor={VERDE} />
        <Kpi titulo="Ainda por atender" valor={reais(m.previsto)} sub={m.porVir + " horários marcados"} cor={AZUL} />
        <Kpi titulo="Perdido com faltas" valor={reais(m.valorFaltas)} sub={m.faltas + " faltas · " + m.taxaFalta + "% de falta"} cor={VERM} />
        <Kpi titulo="Pacotes vendidos" valor={reais(m.valorPacotes)} sub={m.pacotesVendidos + " no mês"} />
        <Kpi titulo="Contatos novos" valor={String(m.novosContatos)} sub="entraram no mês" />
      </div>

      {/* semana + origens */}
      <div style={{ display: "flex", gap: 14, flexWrap: "wrap", marginBottom: 16 }}>
        <div style={{ ...card, flex: "1 1 380px", minWidth: 320 }}>
          {tituloCard("Próximos 7 dias", "Quanto tempo de agenda já está ocupado em cada dia.")}
          {d.semana.map((s, i) => (
            <div key={i} style={{ marginBottom: 11 }}>
              <div style={{ display: "flex", justifyContent: "space-between", gap: 10, fontSize: 12.5, marginBottom: 4 }}>
                <span style={{ color: "var(--text-primary)" }}>
                  {i === 0 ? "Hoje" : s.dia} <span style={{ color: "var(--text-muted)" }}>{fmtDia(s.data)}</span>
                </span>
                <span style={{ color: "var(--text-secondary)", whiteSpace: "nowrap", fontWeight: 600 }}>
                  {s.horarios ? s.horarios + " · " + horas(s.minutos) : "livre"}
                </span>
              </div>
              <div style={{ height: 7, borderRadius: 4, background: "var(--hover-bg)", overflow: "hidden" }}>
                <div style={{ height: "100%", width: Math.max(2, (s.minutos / maiorDia) * 100) + "%", background: ORANGE, borderRadius: 4 }} />
              </div>
            </div>
          ))}
        </div>

        <div style={{ ...card, flex: "1 1 300px", minWidth: 280 }}>
          {tituloCard("De onde vêm os pacientes", "Some o cadastro de pacientes e os contatos que entraram.")}
          <Barras dados={d.origens.map((o) => ({ rotulo: o.rotulo, qtd: o.total }))}
            rotuloValor={(x) => x.qtd} vazio="Ainda sem origem preenchida nos cadastros." />
        </div>
      </div>

      {/* procedimentos + espera */}
      <div style={{ display: "flex", gap: 14, flexWrap: "wrap", marginBottom: 16 }}>
        <div style={{ ...card, flex: "1 1 380px", minWidth: 320 }}>
          {tituloCard("O que mais rendeu no mês", "Procedimentos já atendidos, somando o valor.")}
          <Barras dados={d.procedimentos.map((p) => ({ rotulo: p.rotulo, qtd: p.valor, total: p.total }))}
            rotuloValor={(x) => x.total + "× · " + reaisCurto(x.qtd)} vazio="Nenhum atendimento fechado ainda neste mês." />
        </div>

        <div style={{ ...card, flex: "1 1 300px", minWidth: 280 }}>
          {tituloCard("Encaixe", "Quem está esperando e quais horários vagaram.")}
          <div style={{ display: "flex", gap: 22 }}>
            <div>
              <div style={{ fontSize: 26, fontWeight: 800, color: "var(--text-primary)", lineHeight: 1 }}>{d.espera.aguardando}</div>
              <div style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 4 }}>na lista de espera</div>
            </div>
            <div>
              <div style={{ fontSize: 26, fontWeight: 800, color: d.espera.vagasAbertas ? ORANGE : "var(--text-primary)", lineHeight: 1 }}>{d.espera.vagasAbertas}</div>
              <div style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 4 }}>vagas abertas</div>
            </div>
          </div>
          {d.espera.vagasAbertas > 0 && d.espera.aguardando > 0 && (
            <div style={{ fontSize: 12.5, color: "var(--text-secondary)", marginTop: 14, lineHeight: 1.5 }}>
              Tem horário vago e gente esperando. Abra a Lista de espera e chame a fila.
            </div>
          )}
        </div>
      </div>

      {/* pacotes e aniversarios */}
      <div style={{ display: "flex", gap: 14, flexWrap: "wrap", paddingBottom: 24 }}>
        <div style={{ ...card, flex: "1 1 380px", minWidth: 320 }}>
          {tituloCard("Pacotes perto de vencer", "Sessões pagas que a paciente ainda não usou. Cada uma é uma volta à clínica.")}
          {!d.pacotesAVencer.length && <div style={{ fontSize: 13, color: "var(--text-muted)" }}>Nenhum pacote vencendo nos próximos 30 dias.</div>}
          <div style={{ display: "flex", flexDirection: "column", gap: 7 }}>
            {d.pacotesAVencer.map((p) => (
              <Linha key={p._id}
                esq={fmtDia(p.validade)}
                meio={<>
                  <div style={{ fontSize: 13, fontWeight: 600, color: "var(--text-primary)" }}>{p.pacienteNome}</div>
                  <div style={{ fontSize: 11.5, color: "var(--text-secondary)", marginTop: 1 }}>
                    {p.procedimento} · faltam {p.restam} de {p.totalSessoes}
                  </div>
                </>}
                acao={<button style={btn} onClick={() => abrirWhats(p.telefone,
                  "Olá " + String(p.pacienteNome || "").split(" ")[0] + "! Você ainda tem " + p.restam +
                  (p.restam === 1 ? " sessão" : " sessões") + " de " + p.procedimento +
                  " para usar até " + fmtDia(p.validade) + ". Quer já deixar marcado?")}>Chamar</button>}
              />
            ))}
          </div>
        </div>

        <div style={{ ...card, flex: "1 1 300px", minWidth: 280 }}>
          {tituloCard("Aniversariantes do mês", "Uma mensagem no dia costuma virar agendamento.")}
          {!d.aniversariantes.length && <div style={{ fontSize: 13, color: "var(--text-muted)" }}>Nenhum cadastro com data de nascimento neste mês.</div>}
          <div style={{ display: "flex", flexDirection: "column", gap: 7 }}>
            {d.aniversariantes.map((a, i) => (
              <Linha key={i}
                esq={"dia " + a.dia}
                meio={<div style={{ fontSize: 13, fontWeight: 600, color: "var(--text-primary)" }}>{a.nome}</div>}
                acao={<button style={btn} onClick={() => abrirWhats(a.telefone,
                  "Olá " + String(a.nome || "").split(" ")[0] + "! Passando para desejar um feliz aniversário 🎉 Preparamos um mimo para você aqui na clínica — quer marcar?")}>Parabenizar</button>}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
